import { ServiceLine } from 'api/types/service.types';
import Button from 'components/atoms/Button';
import BottomSheet from 'components/molecules/BottomSheet';
import { BUTTON_NAMES } from 'constant/button-names';
import { useConfig } from 'context/app-config';
import { selectCurrentOrder, selectPreference, selectSelectedServices, selectServiceLineCollectionMethods } from 'pages/newOrder/newOrder.selectors';
import { updatePreferences } from 'pages/newOrder/newOrder.slice';
import React, { useCallback, useRef } from 'react';
import { Col, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'redux-stores/reducers/rootReducer';
import store from 'redux-stores/store';
import styled, { useTheme } from 'styled-components';

import DriverInstructions from './DriverInstructions';

const StyledFooterRow = styled.div`
  position: sticky;
  bottom: 0;
  padding: 16px;
  padding-bottom: 20px;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: 1;
`;

interface DriverInstructionsBottomSheetProps {
  show: boolean;
  onClose?: () => any;
}

const StyledCol = styled(Col)`
  margin-top: 1px;
`;

export const DriverInstructionsBottomSheet: React.FC<DriverInstructionsBottomSheetProps> = ({ show, onClose }) => {
  const config = useConfig();
  const { t } = useTranslation(undefined, { useSuspense: false });
  const hasSupportForMultipleDropoffs = config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs;
  const selectedServiceLines = useSelector(selectSelectedServices);
  const theme = useTheme();
  const dispatch = useDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const serviceLineDropoffMethods = useSelector(selectServiceLineCollectionMethods);
  const { isDropoffAtDoor: defaultDropOffAtDoor, isPickupAtDoor } = useSelector(selectCurrentOrder);

  const formRefs = useRef<{
    [key in ServiceLine]?: React.RefObject<HTMLFormElement>;
  }>({
    [ServiceLine.LAUNDRY_SERVICE]: React.createRef<HTMLFormElement>(),
    [ServiceLine.SHOE_SERVICE]: React.createRef<HTMLFormElement>(),
  });

  const onPreferenceInstructionsSave = useCallback(
    (optionType: 'driverDropoffInstructions' | 'driverPickupInstructions', form: string[], serviceLine?: ServiceLine) => {
      const state: RootState = store.getState();
      const preferences = selectPreference(state, undefined, serviceLine);
      const selectedValues = form.map((value) =>
        value.replace(`${serviceLine}-${optionType === 'driverDropoffInstructions' ? 'dropoff' : 'pickup'}-`, '')
      );
      if (!preferences) return;

      const isDropoffAtDoor = serviceLine ? serviceLineDropoffMethods[serviceLine]?.isDropoffAtDoor : defaultDropOffAtDoor;

      dispatch(
        updatePreferences({
          serviceLine,
          ...preferences,
          [optionType]: {
            ...preferences[optionType],
            ...((optionType === 'driverPickupInstructions' ? isPickupAtDoor : isDropoffAtDoor)
              ? {
                  atDoor: {
                    categories: preferences[optionType].atDoor.categories.map((category) => ({
                      ...category,
                      options: category.options.map((option) => ({
                        ...option,
                        value: selectedValues.includes(option.key),
                      })),
                    })),
                  },
                }
              : {
                  inPerson: {
                    categories: preferences[optionType].inPerson.categories.map((category) => ({
                      ...category,
                      options: category.options.map((option) => ({
                        ...option,
                        value: selectedValues.includes(option.key),
                      })),
                    })),
                  },
                }),
          },
        })
      );
    },
    [defaultDropOffAtDoor, dispatch, isPickupAtDoor, serviceLineDropoffMethods]
  );

  const handleSubmit = () => {
    if (!formRefs.current) return;

    const getValues = (elements: HTMLFormControlsCollection) =>
      Array.from(elements)
        // @ts-ignore
        .filter((el) => el.checked)
        .map((el) => el.getAttribute('value') || '');

    const handleFormSubmission = (ref: React.MutableRefObject<HTMLFormElement | null> | undefined, serviceLine?: ServiceLine) => {
      if (!ref || !ref.current) return;

      const { elements } = ref.current;
      const options = getValues(elements);

      let collectionInstruction = options[0] || null;
      let deliveryInstruction = options[1] || null;

      if (options.length === 1) {
        deliveryInstruction = collectionInstruction;
        collectionInstruction = null;
      }

      if (collectionInstruction) onPreferenceInstructionsSave('driverPickupInstructions', [collectionInstruction], serviceLine);

      if (deliveryInstruction) onPreferenceInstructionsSave('driverDropoffInstructions', [deliveryInstruction], serviceLine);
    };

    if (hasSupportForMultipleDropoffs) {
      for (const serviceLine of selectedServiceLines) {
        handleFormSubmission(formRefs.current[serviceLine], serviceLine);
      }
    } else {
      handleFormSubmission(formRef);
    }

    onClose?.();
  };

  return (
    <BottomSheet hideCloseButton show={show} onClose={onClose} title={t('homePage.driverInstructionsTitle')} maxHeight="80%" hasNoSpacing>
      <Container>
        <StyledCol className="padding-start-0 padding-end-0">
          {hasSupportForMultipleDropoffs ? (
            selectedServiceLines.map((serviceLine, index) => (
              <DriverInstructions
                key={`${serviceLine}-driver-instructions`}
                serviceLine={serviceLine}
                formRef={formRefs.current[serviceLine]!!}
                showCollection={index === 0}
              />
            ))
          ) : (
            <DriverInstructions showCollection formRef={formRef} />
          )}
        </StyledCol>
      </Container>
      <StyledFooterRow>
        <Button
          textColor={theme.components.pages.pickupDropoff?.button?.textColor}
          backgroundColor={theme.components.pages.pickupDropoff?.button?.background}
          block
          size="lg"
          onClick={handleSubmit}
          buttonName={BUTTON_NAMES.APPLY}
        >
          {t('newOrderPage.apply')}
        </Button>
      </StyledFooterRow>
    </BottomSheet>
  );
};
